import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Leaf, Lock, Sparkles, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergenceParcours() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [parcours, setParcours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userProgress, setUserProgress] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const isFrench = i18n.language === 'fr';

  useEffect(() => {
    const email = localStorage.getItem("emergence_email");
    if (email) {
      setUserEmail(email);
      checkPremiumStatus(email);
      fetchProgress(email);
    }
    fetchParcours();
  }, []);

  const fetchParcours = async () => {
    try {
      const response = await axios.get(`${API}/emergence/parcours?language=${i18n.language}`);
      setParcours(response.data.parcours);
    } catch (error) {
      console.error("Error fetching parcours:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPremiumStatus = async (email) => {
    try {
      const response = await axios.get(`${API}/emergence/premium/status?email=${encodeURIComponent(email)}`);
      setIsPremium(response.data.is_premium);
    } catch (error) {
      console.error("Error checking premium:", error);
    }
  };

  const fetchProgress = async (email) => {
    try {
      const response = await axios.get(`${API}/emergence/parcours/progress?email=${encodeURIComponent(email)}`);
      setUserProgress(response.data);
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  const handleStartParcours = async (parcoursId) => {
    if (!userEmail) {
      toast.error(isFrench ? "Connecte-toi d'abord via le diagnostic" : "Connect first via the diagnostic");
      navigate("/emergence/diagnostic");
      return;
    }

    if (!isPremium) {
      handleUpgrade();
      return;
    }

    try {
      await axios.post(`${API}/emergence/parcours/start`, {
        email: userEmail,
        parcours_id: parcoursId
      });
      navigate(`/emergence/parcours/${parcoursId}/day/1`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isFrench ? "Erreur" : "Error"));
    }
  };

  const handleUpgrade = async () => {
    if (!userEmail) {
      toast.error(isFrench ? "Entre ton email d'abord" : "Enter your email first");
      return;
    }

    setCheckingOut(true);
    try {
      const response = await axios.post(`${API}/emergence/premium/checkout?email=${encodeURIComponent(userEmail)}&language=${i18n.language}`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(isFrench ? "Erreur lors du paiement" : "Payment error");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3B7A8C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="emergence-bg min-h-screen">
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/emergence" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? "Retour" : "Back"}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-[#3B7A8C]" />
            <span className="font-heading text-lg text-[#2D5A66]">{isFrench ? "Parcours guidés" : "Guided journeys"}</span>
          </div>
          {isPremium ? (
            <span className="emergence-badge">
              <Sparkles className="w-4 h-4 mr-1" />
              Premium
            </span>
          ) : (
            <Button 
              onClick={handleUpgrade}
              disabled={checkingOut}
              className="emergence-btn-accent text-sm"
            >
              {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : (isFrench ? "Devenir Premium" : "Go Premium")}
            </Button>
          )}
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#2D5A66] mb-4">
              {isFrench ? "Parcours de 21 jours" : "21-Day Journeys"}
            </h1>
            <p className="text-[#7A8B99] max-w-xl mx-auto">
              {isFrench 
                ? "Des parcours complets pour t'accompagner dans ta transformation, jour après jour, à ton rythme."
                : "Complete journeys to support your transformation, day by day, at your own pace."}
            </p>
          </div>

          {/* Parcours actif */}
          {userProgress?.active_parcours && (
            <Card className="emergence-card border-0 mb-8 bg-gradient-to-br from-[#3B7A8C]/10 to-[#8FB8A8]/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#3B7A8C] font-medium mb-1">
                      {isFrench ? "🌱 Parcours en cours" : "🌱 Current journey"}
                    </p>
                    <h3 className="font-heading text-xl text-[#2D5A66]">
                      {parcours.find(p => p.id === userProgress.active_parcours.parcours_id)?.title}
                    </h3>
                    <p className="text-[#7A8B99] text-sm">
                      {isFrench ? `Jour ${userProgress.active_parcours.current_day} / 21` : `Day ${userProgress.active_parcours.current_day} / 21`}
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate(`/emergence/parcours/${userProgress.active_parcours.parcours_id}/day/${userProgress.active_parcours.current_day}`)}
                    className="emergence-btn-primary"
                  >
                    {isFrench ? "Continuer" : "Continue"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Liste des parcours */}
          <div className="grid gap-6">
            {parcours.map((p) => {
              const isCompleted = userProgress?.completed_parcours?.some(cp => cp.parcours_id === p.id);
              const isActive = userProgress?.active_parcours?.parcours_id === p.id;
              
              return (
                <Card 
                  key={p.id} 
                  className={`emergence-card border-0 overflow-hidden ${isActive ? 'ring-2 ring-[#3B7A8C]' : ''}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col sm:flex-row">
                      {/* Icon side */}
                      <div 
                        className="sm:w-32 h-24 sm:h-auto flex items-center justify-center text-5xl"
                        style={{ backgroundColor: `${p.color}15` }}
                      >
                        {p.icon}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-heading text-xl font-semibold text-[#2D5A66] mb-1">
                              {p.title}
                            </h3>
                            <p className="text-[#7A8B99] text-sm mb-3">{p.subtitle}</p>
                            <p className="text-[#3D4F5F] text-sm leading-relaxed">{p.description}</p>
                          </div>
                          {isCompleted && (
                            <CheckCircle className="w-6 h-6 text-[#8FB8A8] flex-shrink-0" />
                          )}
                        </div>
                        
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-xs text-[#7A8B99]">
                            {p.days_count} {isFrench ? "jours" : "days"}
                          </span>
                          
                          {isPremium ? (
                            <Button 
                              onClick={() => handleStartParcours(p.id)}
                              disabled={isActive}
                              className={isActive ? "bg-[#3B7A8C]/50" : "emergence-btn-primary"}
                              size="sm"
                            >
                              {isActive 
                                ? (isFrench ? "En cours" : "In progress")
                                : isCompleted 
                                  ? (isFrench ? "Refaire" : "Redo")
                                  : (isFrench ? "Commencer" : "Start")}
                              {!isActive && <ArrowRight className="w-4 h-4 ml-2" />}
                            </Button>
                          ) : (
                            <Button 
                              onClick={handleUpgrade}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-[#D4A574] text-[#D4A574]"
                            >
                              <Lock className="w-4 h-4 mr-2" />
                              Premium
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA Premium */}
          {!isPremium && (
            <Card className="emergence-card border-0 mt-8 bg-gradient-to-br from-[#D4A574]/10 to-[#D4A574]/5">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-10 h-10 text-[#D4A574] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-semibold text-[#2D5A66] mb-2">
                  {isFrench ? "Passer à Premium" : "Upgrade to Premium"}
                </h3>
                <p className="text-[#7A8B99] mb-6 max-w-md mx-auto">
                  {isFrench 
                    ? "Accède à tous les parcours guidés, au chat illimité avec ton coach, et au journal intelligent complet."
                    : "Access all guided journeys, unlimited coach chat, and the complete smart journal."}
                </p>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-4xl font-bold text-[#2D5A66]">19,99€</span>
                  <span className="text-[#7A8B99]">/ {isFrench ? "mois" : "month"}</span>
                </div>
                <Button 
                  onClick={handleUpgrade}
                  disabled={checkingOut}
                  className="emergence-btn-accent text-lg px-8 py-4"
                >
                  {checkingOut ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {isFrench ? "Devenir Premium" : "Go Premium"}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
